import { ButtonDropdown, ButtonV2, DataGridRef, FilterV2, SearchV2, SortV2 } from '@cimplico/components';
import { useMemo, useRef, useState } from 'react';
import { Entity } from '../types/entity';
import { useIsActiveSubscription } from '../hooks/use-is-active-subscription';
import { useIsFirmRole } from '../hooks/use-is-firm-role';
import PageContainer from '../components/page-container/PageContainer';
import PageTitle from '../components/PageTitle';
import SecondaryPageActions from '../components/secondary-page-actions/SecondaryPageActions';
import AssignToGroupConfirm from '../features/entities/AssignToGroupConfirm';
import EntitiesArchiveConfirm from '../features/entities/EntitiesArchiveConfirm';
import EntitiesTable from '../features/entities/EntitiesTable';
import RemoveFromGroup from '../features/entities/RemoveFromGroup';
import EntitiesUnarchiveConfirm from '../features/entities/EntitiesUnarchiveConfirm';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFirmContext } from '../providers/FirmProvider';
import { processQueryString } from '../features/entities/utils';
import { useGroups } from '../queries/use-groups';
import GrowHeightContainer from '../components/GrowHeightContainer';
import { ArrowDownTrayIcon } from '@heroicons/react/20/solid';
import { useExportEntities } from '../queries/use-entities-export';
import { toastNotification } from '../components/toast/Toast';
import { useIntercom } from 'react-use-intercom';
import { intercomEvents } from '../intercom-events';

const sortOptions = [
    { value: 'name', label: 'Name' },
    { value: 'type', label: 'Type' },
    { value: 'groupName', label: 'Group' },
    { value: 'updatedAt', label: 'Last Updated' },
    { value: 'activeWorkpapers', label: 'Open Workpapers' },
];
const filterTypeOptions = [
    { defaultActive: false, label: 'Company', value: 'COMPANY' },
    { defaultActive: false, label: 'Partnership', value: 'PARTNERSHIP' },
    { defaultActive: false, label: 'Sole Trader', value: 'SOLE_TRADER' },
    { defaultActive: false, label: 'Trust', value: 'TRUST' },
    { defaultActive: false, label: 'Individual', value: 'INDIVIDUAL' },
    { defaultActive: false, label: 'Unit Trust', value: 'UNIT_TRUST' },
    { defaultActive: false, label: 'Estate', value: 'ESTATE' },
    {
        defaultActive: false,
        label: 'Self Managed Superannuation Fund',
        value: 'SELF_MANAGED_SUPERANNUATION_FUND',
    },
    { defaultActive: false, label: 'Superannuation Fund', value: 'SUPERANNUATION_FUND' },
    { defaultActive: false, label: 'Government Entity', value: 'GOVERNMENT_ENTITY' },
    { defaultActive: false, label: 'Club Or Society', value: 'CLUB_OR_SOCIETY' },
    { defaultActive: false, label: 'Not For Profit', value: 'NOT_FOR_PROFIT' },
    { defaultActive: false, label: 'Accounting Practice', value: 'ACCOUNTING_PRACTICE' },
    { defaultActive: false, label: 'Charity', value: 'CHARITY' },
    { defaultActive: false, label: 'Look Through Company', value: 'LOOK_THROUGH_COMPANY' },
    { defaultActive: false, label: 'S Corporation', value: 'S_CORPORATION' },
    { defaultActive: false, label: 'Other', value: 'OTHER' },
];

function EntitiesPage(): JSX.Element {
    const { trackEvent } = useIntercom();
    const navigate = useNavigate();
    const groupsQuery = useGroups({ page: 1, search: '', sort: '', filter: 'entities[gt]=0' });

    const [search, setSearch] = useState('');
    const [sort, setSort] = useState('');
    const [filter, setFilter] = useState('');
    const [selectedEntities, setSelectedEntities] = useState<Entity[]>([]);
    const [secondaryAction, setSecondaryAction] = useState<string | undefined>();

    const activeSubscription = useIsActiveSubscription();
    const { isExternalUser } = useIsFirmRole();

    const tableRef = useRef<DataGridRef | null>(null);

    function clearAction(): void {
        setSecondaryAction(undefined);
    }
    function resetTable(): void {
        clearAction();
        setSelectedEntities([]);
        tableRef?.current?.clear();
        tableRef?.current?.resetPagination();
    }

    const removeFromGroup = useMemo(() => selectedEntities.filter((e) => e.groups.length > 0), [selectedEntities]);
    const canArchiveEntities = useMemo(
        () => selectedEntities.filter((e) => e.activeWorkpapers.length === 0 && !e.isArchived),
        [selectedEntities]
    );

    const canUnarchiveEntities = useMemo(() => selectedEntities.filter((e) => e.isArchived), [selectedEntities]);
    function redirectToNewEntity(provider?: string): void {
        if (provider) {
            navigate(`new?provider=${provider}`);
        } else {
            navigate('new');
        }
    }

    const { integrations } = useFirmContext();
    const fyiIntegration = Boolean(integrations.fyi?.cabinet);

    const downloadQuery = useExportEntities({ search, sort, filter });

    function download(): void {
        downloadQuery.mutate(undefined, {
            onSuccess: () => {
                // Send to intercom
                trackEvent(intercomEvents.entities.exportDataToCsv.event);
            },
            onError: (error) => {
                toastNotification('error', `${error.message}`);
            },
        });
    }
    return (
        <PageContainer>
            <AssignToGroupConfirm
                entities={selectedEntities}
                isOpen={secondaryAction === 'ASSIGN'}
                onClose={clearAction}
                onAssign={resetTable}
            />
            <RemoveFromGroup entities={removeFromGroup} isOpen={secondaryAction === 'REMOVE'} onClose={clearAction} onRemove={resetTable} />
            <EntitiesArchiveConfirm
                entities={canArchiveEntities}
                isOpen={secondaryAction === 'ARCHIVE'}
                onClose={clearAction}
                onArchive={resetTable}
            />
            <EntitiesUnarchiveConfirm
                entities={canUnarchiveEntities}
                isOpen={secondaryAction === 'UNARCHIVE'}
                onClose={clearAction}
                onUnarchive={resetTable}
            />
            <div className="pb-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <PageTitle title="Entities" />

                    {selectedEntities.length === 0 && (
                        <ButtonDropdown
                            variant="primary"
                            label="New Entity"
                            onClick={redirectToNewEntity}
                            size="sm"
                            disabled={!activeSubscription || isExternalUser}
                        >
                            {fyiIntegration && (
                                <ButtonDropdown.SecondaryAction
                                    label="Add from FYI"
                                    onClick={(): void => redirectToNewEntity('fyi')}
                                    disabled={!activeSubscription || isExternalUser}
                                />
                            )}
                        </ButtonDropdown>
                    )}
                    {selectedEntities.length > 0 && (
                        <SecondaryPageActions maxActionsCount={4}>
                            {removeFromGroup.length > 0 && (
                                <SecondaryPageActions.Button
                                    label={`Remove from Group (${removeFromGroup.length})`}
                                    disabled={!activeSubscription || isExternalUser}
                                    onClick={(): void => setSecondaryAction('REMOVE')}
                                />
                            )}

                            <SecondaryPageActions.Button
                                label={`Assign to Group (${selectedEntities.length})`}
                                onClick={(): void => setSecondaryAction('ASSIGN')}
                                disabled={!activeSubscription || isExternalUser}
                            />

                            {canUnarchiveEntities.length > 0 && (
                                <SecondaryPageActions.Button
                                    label={`Unarchive (${canUnarchiveEntities.length})`}
                                    disabled={!activeSubscription || isExternalUser}
                                    onClick={(): void => setSecondaryAction('UNARCHIVE')}
                                />
                            )}

                            {canArchiveEntities.length > 0 && (
                                <SecondaryPageActions.Button
                                    type="danger"
                                    label={`Archive (${canArchiveEntities.length})`}
                                    disabled={!activeSubscription || isExternalUser}
                                    onClick={(): void => setSecondaryAction('ARCHIVE')}
                                />
                            )}
                        </SecondaryPageActions>
                    )}
                </div>

                <section>
                    <div className="border-b">
                        <div className="inline-flex gap-4 h-11  " />
                    </div>
                    <GrowHeightContainer className="flex flex-wrap items-center gap-2 mt-4">
                        <FilterV2 onChange={({ queryString }): void => setFilter(processQueryString(queryString))}>
                            <FilterV2.CheckboxFilter id="type" label="Type" options={filterTypeOptions} />
                            <FilterV2.CheckboxFilter
                                id="billingActive"
                                label="Billing Status"
                                options={[
                                    { defaultActive: false, label: 'Active', value: 'true' },
                                    { defaultActive: false, label: 'Inactive', value: 'false' },
                                ]}
                            />
                            <FilterV2.ToggleFilter prefix="is" id="isArchived" label="Archived" />
                            <FilterV2.SelectFilter
                                id="group"
                                label="Group"
                                values={[]}
                                options={(groupsQuery.data?.groups ?? []).map((group) => ({
                                    active: false,
                                    label: group.name,
                                    value: group.id,
                                }))}
                            />
                            <FilterV2.DateFilter id="updatedAt" label="Last Updated" />
                            <FilterV2.NumericFilter id="activeWorkpapers" label="Open Workpapers" />
                            <FilterV2.CheckboxFilter
                                id="provider"
                                label="Provider"
                                options={[
                                    { defaultActive: false, label: 'Xero', value: 'XERO' },
                                    { defaultActive: false, label: 'FYI', value: 'FYI' },
                                ]}
                            />
                        </FilterV2>
                        <SearchV2 onChange={setSearch} placeholder="Search..." />
                        <SortV2 onChange={({ queryString }): void => setSort(queryString)} options={sortOptions} defaultValue={'name'} />
                        <div className="ml-auto">
                            <ButtonV2
                                leftIcon={<ArrowDownTrayIcon />}
                                type="secondary"
                                size="sm"
                                label="Export"
                                onClick={download}
                                isDisabled={downloadQuery.isLoading}
                            />
                        </div>
                    </GrowHeightContainer>
                </section>
            </div>
            <section className="mt-4">
                <EntitiesTable search={search} sort={sort} filter={filter} onSelect={setSelectedEntities} tableRef={tableRef} />
            </section>
        </PageContainer>
    );
}

export default EntitiesPage;
